import { useEffect, useState } from 'react'
import { paymentService } from '@cannect/services/paymentService'

interface UsePagaleveEligibilityParams {
  cpf?: string
  amount?: number
  phone?: string
  email?: string
  enabled?: boolean
}

interface UsePagaleveEligibilityReturn {
  isEligible: boolean
  isChecking: boolean
  error: string | null
  recheckEligibility: () => Promise<void>
}

/**
 * Hook para verificar elegibilidade do cliente para Pagaleve
 * @param cpf - CPF do cliente
 * @param amount - Valor total da compra
 * @param phone - Telefone do cliente
 * @param email - Email do cliente
 * @param enabled - Se true, faz a verificação automaticamente. Default: true
 */
export function usePagaleveEligibility({
  cpf,
  amount,
  phone,
  email,
  enabled = true
}: UsePagaleveEligibilityParams): UsePagaleveEligibilityReturn {
  const [isEligible, setIsEligible] = useState(false)
  const [isChecking, setIsChecking] = useState(() => {
    const hasAllData = Boolean(enabled && cpf && amount && amount >= 30 && phone && email)
    return hasAllData
  })
  const [error, setError] = useState<string | null>(null)

  const checkEligibility = async () => {
    if (!cpf || !amount || !phone || !email) {
      setIsEligible(false)
      setIsChecking(false)
      return
    }

    if (amount < 30) {
      setIsEligible(false)
      setIsChecking(false)
      return
    }

    setIsChecking(true)
    setError(null)

    try {
      const eligible = await paymentService.checkPagaleveEligibility({
        cpf,
        amount,
        phone,
        email
      })

      setIsEligible(eligible)
    } catch (err: any) {
      setError(err.message || 'Erro ao verificar elegibilidade')
      setIsEligible(false)
      console.error('Erro ao verificar elegibilidade Pagaleve:', err)
    } finally {
      setIsChecking(false)
    }
  }

  useEffect(() => {
    const hasAllData = enabled && cpf && amount && amount >= 30 && phone && email

    if (hasAllData) {
      const timer = setTimeout(() => {
        checkEligibility()
      }, 500)

      return () => clearTimeout(timer)
    }

    setIsEligible(false)
    setIsChecking(false)
    return undefined
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cpf, amount, phone, email, enabled])

  return {
    isEligible,
    isChecking,
    error,
    recheckEligibility: checkEligibility
  }
}
